import React from "react";
import { Link } from "react-router-dom";

// Small session-drift on the "12,600 reports" pill so the number feels alive.
function bumpCount(base) {
  const drift = Math.floor(base * (0.001 + Math.random() * 0.004)) + Math.floor(Math.random() * 8);
  return (base + drift).toLocaleString("en-IN");
}

/**
 * PremiumTeaser — shimmer-band CTA card shown after a FREE tool's result
 * to funnel warm leads into the paid 20-Year Report.
 *
 * Props
 *  - topLine    : ReactNode — bold personalised hook using data the user
 *                 just saw (e.g. "Your Aries lagna + Ashwini nakshatra tell
 *                 only 5% of your story.")
 *  - subLine    : ReactNode — soft-benefit body copy, ~1-2 lines
 *  - ctaLabel   : string   (default "Reveal My 20-Year Story →")
 *  - testid     : string   suffix for the data-testid
 */
export default function PremiumTeaser({
  topLine,
  subLine,
  ctaLabel = "Reveal My 20-Year Story →",
  testid = "premium-teaser",
}) {
  const liveCount = React.useMemo(() => bumpCount(12600), []);
  return (
    <Link
      to="/twenty-year-prediction"
      data-testid={testid}
      className="group block relative overflow-hidden rounded-md ring-1 ring-amber-300/70 shadow-[0_8px_28px_-8px_rgba(255,140,60,0.55)] transition-transform duration-300 hover:-translate-y-0.5"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1440] via-[#1a1a4e] to-[#050917]" />
      <div
        className="absolute inset-0 opacity-60 mix-blend-overlay"
        style={{
          background:
            "linear-gradient(115deg, #fbbf24 0%, #f97316 30%, #ec4899 60%, #fbbf24 100%)",
          backgroundSize: "300% 100%",
          animation: "shimmer-band 8s linear infinite",
        }}
      />
      <div className="relative p-5 sm:p-6 text-amber-50">
        <div className="flex items-center gap-1.5 text-[10px] font-black tracking-widest text-amber-200 uppercase">
          <span>❤️</span>
          <span>Most Liked · 10 Lakh customers · {liveCount} reports</span>
        </div>
        <div className="font-serif-display text-xl sm:text-2xl font-light mt-1.5 leading-tight">
          {topLine}
        </div>
        {subLine && (
          <p className="text-xs sm:text-sm text-amber-100/80 mt-2 leading-relaxed">
            {subLine}
          </p>
        )}
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-amber-200/70 line-through text-sm">₹5500</span>
            <span className="font-serif-display text-2xl sm:text-3xl text-amber-50">
              ₹899
            </span>
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/20 backdrop-blur-sm">
              One-time
            </span>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-bold tracking-widest uppercase bg-gradient-to-r from-amber-300 to-yellow-400 text-amber-950 px-3 py-1.5 rounded-full shadow group-hover:scale-105 transition-transform">
            {ctaLabel}
          </span>
        </div>
      </div>
    </Link>
  );
}
